import { authSession, type FidexaSession } from "@/utils/authSession";
import { jwtDecode } from "jwt-decode";
import { posthog } from "./posthogClient";

// Diagnóstico de sessão ("deslogou sozinho").
//
// Clientes relatam que a Fidexa desloga em momentos que ninguém consegue
// reproduzir. Do lado do servidor, cada 401 do refresh agora é logado com o
// motivo real; deste lado, registramos no PostHog (e no console) cada passo
// que pode terminar a sessão — o 401 que disparou a renovação, a falha do
// refresh (status, código, rede) e o encerramento em si — enriquecidos com
// o session_id do JWT, para cruzar com o log do NotiifyMe pelo mesmo id.
//
// Nada aqui altera o comportamento de autenticação: é só observação.

export type AuthDiagnosticEvent =
  // Um request autenticado recebeu 401 e vai tentar renovar.
  | "auth_request_401"
  // Renovação concluída (com ou sem request de rede).
  | "auth_refresh_ok"
  // Renovação falhou; `will_logout` diz se a sessão será encerrada.
  | "auth_refresh_failed"
  // Storage limpo e página recarregada: a sessão acabou.
  | "auth_session_ended";

type TokenClaims = {
  nameid?: string;
  session_id?: string;
  permissions_version?: string;
  iat?: number;
  exp?: number;
};

const decodeClaims = (token: string | null): TokenClaims | null => {
  if (!token) return null;
  try {
    return jwtDecode<TokenClaims>(token);
  } catch {
    return null;
  }
};

const minutesFromNow = (epochSeconds: number | undefined): number | null =>
  typeof epochSeconds === "number"
    ? Math.round(((epochSeconds * 1000 - Date.now()) / 60_000) * 10) / 10
    : null;

/**
 * Contexto comum a todo evento: identidade da sessão (do JWT em storage),
 * idade/validade do access token pelo relógio do CLIENTE (um relógio errado
 * aparece aqui como `token_expires_in_min` incoerente com o servidor),
 * estado da aba e capacidades do navegador que o fluxo de refresh depende.
 */
const buildContext = (): Record<string, unknown> => {
  let accessToken: string | null = null;
  let hasRefreshToken = false;
  let session: FidexaSession | null = null;

  try {
    accessToken = localStorage.getItem("access_token");
    hasRefreshToken = Boolean(localStorage.getItem("refresh_token"));
    session = authSession.getActiveSession();
  } catch {
    // storage indisponível: o próprio evento já é o sintoma
  }

  const claims = decodeClaims(accessToken);

  return {
    session_id: session?.session_id ?? claims?.session_id ?? null,
    token_user_id: session?.user_id ?? claims?.nameid ?? null,
    permissions_version:
      session?.permissions_version ?? claims?.permissions_version ?? null,
    token_issued_min_ago:
      claims?.iat !== undefined ? -(minutesFromNow(claims.iat) ?? 0) : null,
    token_expires_in_min: minutesFromNow(session?.expires_at ?? claims?.exp),
    has_access_token: Boolean(accessToken),
    has_refresh_token: hasRefreshToken,
    page_path:
      typeof window !== "undefined" ? window.location.pathname : null,
    visibility:
      typeof document !== "undefined" ? document.visibilityState : null,
    online: typeof navigator !== "undefined" ? navigator.onLine : null,
    locks_supported:
      typeof navigator !== "undefined" && "locks" in navigator,
    client_time: new Date().toISOString(),
  };
};

/**
 * Registra um evento de diagnóstico de sessão. `sendInstantly` força o
 * envio imediato — obrigatório quando um reload vem logo em seguida, senão o
 * batch do PostHog é descartado junto com a página.
 */
export const reportAuthEvent = (
  event: AuthDiagnosticEvent,
  properties: Record<string, unknown> = {},
  options: { sendInstantly?: boolean } = {}
): void => {
  if (typeof window === "undefined") return;

  const payload = { ...buildContext(), ...properties };

  // Falha de telemetria nunca pode interferir no fluxo de autenticação.
  try {
    posthog.capture(event, payload, {
      send_instantly: options.sendInstantly === true,
    });
  } catch {
    // ignorado de propósito
  }

  console.warn(`[auth] ${event}`, payload);
};
